import { Injectable, inject } from '@angular/core';
import { IFile } from '@app-root-interfaces';
import { DownloadService, InformationService } from '@app-root-services';
import { downloadBlob } from '../../../shared/helper/download.helper';
import { appReducer, profileSelectors } from '@app-root-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { errorActions } from '../actions';
import * as downloadActions from '../actions/download.actions';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { TranslateService } from '@ngx-translate/core';
import { InformationType } from '@scf-monorepo/interfaces';

@Injectable()
export class DownloadEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store<appReducer.AppState>);
    private downloadService = inject(DownloadService);
    private informationService = inject(InformationService);
    private toastService = inject(SiToastNotificationService);
    private translate = inject(TranslateService);

    showDownloadError$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.getFileFailure),
            map(() =>
                errorActions.addError({
                    error: {
                        obj: null,
                        isCritical: false,
                        text: 'file-download-error'
                    }
                })
            )
        )
    );

    downloadFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.downloadFile),
            withLatestFrom(this.store$.pipe(select(profileSelectors.selectProfileSelectedProfile))),
            mergeMap(([{ file }, profile]) => {
                if (!profile.isValid) {
                    return of(downloadActions.downloadNotAllowed());
                }

                return this.downloadFileForProfile(file).pipe(
                    switchMap((data) =>
                        of(
                            downloadActions.getIniFile({ file }),
                            downloadActions.saveFile({ file, payload: new Blob([data]) })
                        )
                    ),
                    catchError(() => of(downloadActions.getFileFailure()))
                );
            })
        )
    );

    downloadSnippetFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.downloadSnippetFile),
            withLatestFrom(this.store$.pipe(select(profileSelectors.selectProfileSelectedProfile))),
            mergeMap(([{ file, formData, language }, profile]) => {
                if (!profile.isValid) {
                    return of(downloadActions.downloadNotAllowed());
                }

                return of(
                    downloadActions.getMetadata({
                        file,
                        formData,
                        language
                    })
                ).pipe(catchError(() => of(downloadActions.getFileFailure())));
            })
        )
    );

    getIniFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.getIniFile),
            filter(({ file }) => !!file.metaInfo.iniFile),
            mergeMap(({ file }) =>
                this.downloadFileForProfile({ name: file.metaInfo.iniFile }).pipe(
                    map((data) =>
                        downloadActions.saveFile({
                            file: { name: file.metaInfo.iniFile } as IFile,
                            payload: new Blob([data])
                        })
                    ),
                    catchError((_error) => of(downloadActions.getFileFailure()))
                )
            )
        )
    );

    saveFile$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(downloadActions.saveFile),
                map(({ file, payload }) => {
                    const fileName = file.name || file.fileName || 'download';
                    downloadBlob(payload, fileName);
                    return {};
                })
            ),
        { dispatch: false }
    );

    getMetadata$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.getMetadata),
            withLatestFrom(this.store$.pipe(select(profileSelectors.selectProfileSelectedProfile))),
            concatMap(([{ file, formData, language }, profile]) =>
                this.informationService.getAllInformations(language, profile).pipe(
                    map((informations) =>
                        downloadActions.fillFile({
                            file,
                            language,
                            formData: {
                                ...informations,
                                ...formData
                            }
                        })
                    ),
                    catchError((error) => {
                        console.error(error);
                        return of(downloadActions.getFileFailure());
                    })
                )
            )
        )
    );

    fillFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.fillFile),
            withLatestFrom(this.store$.pipe(select(profileSelectors.selectProfileSelectedProfile))),
            concatMap(([{ file, formData, language }, profile]) =>
                this.downloadService.downloadFilledFile(file, formData, profile, language).pipe(
                    mergeMap((response) => {
                        if (typeof response === 'string') {
                            return of(downloadActions.fileSentByEmail({ message: response }));
                        }

                        const { data, fileNameSuffix, emailMessage } = response;
                        const updatedFile = {
                            ...file,
                            name: this.createFileName(file, fileNameSuffix)
                        } as IFile;

                        const actions = [];
                        if (emailMessage) {
                            actions.push(downloadActions.fileSentByEmail({ message: emailMessage }));
                        }
                        actions.push(
                            downloadActions.fileDownloadReady({
                                file: updatedFile,
                                data,
                                shouldGetIniFile: !!file.metaInfo?.iniFile
                            })
                        );
                        return from(actions);
                    }),
                    catchError((error) => {
                        console.error('Error filling file:', error);
                        return of(downloadActions.getFileFailure());
                    })
                )
            )
        )
    );

    handleFileSentByEmail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.fileSentByEmail),
            map(({ message: _message }) => {
                this.toastService.queueToastNotification(
                    'success',
                    '',
                    this.translate.instant('file-sent-per-mail-successfully')
                );
                return downloadActions.fileSentByEmailSuccess();
            })
        )
    );

    handleFileDownloadReady$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.fileDownloadReady),
            switchMap(({ file, data, shouldGetIniFile }) => {
                const actions = [];

                if (shouldGetIniFile) {
                    actions.push(downloadActions.getIniFile({ file }));
                }

                actions.push(
                    downloadActions.saveFile({
                        file,
                        payload: new Blob([data])
                    })
                );

                return actions;
            })
        )
    );

    downloadManagementInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.downloadManagementInformation),
            switchMap(() =>
                this.informationService
                    .download(InformationType.Management)
                    .pipe(map(() => downloadActions.downloadManagementInformationSuccess()))
            )
        )
    );

    downloadLegalInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.downloadLegalInformation),
            switchMap(() =>
                this.informationService
                    .download(InformationType.Legal)
                    .pipe(map(() => downloadActions.downloadLegalInformationSuccess()))
            )
        )
    );

    downloadLocationInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.downloadLocationInformation),
            switchMap(() =>
                this.informationService
                    .download(InformationType.Location)
                    .pipe(map(() => downloadActions.downloadLocationInformationSuccess()))
            )
        )
    );

    downloadTranslationInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadActions.downloadTranslationInformation),
            switchMap(() =>
                this.downloadService
                    .downloadTranslationInformation()
                    .pipe(map(() => downloadActions.downloadTranslationInformationSuccess()))
            )
        )
    );

    private downloadFileForProfile(file: IFile) {
        return this.store$.pipe(
            select(profileSelectors.selectProfileUserProfile),
            switchMap((user) =>
                user.country === 'CN'
                    ? this.downloadService.downloadBase64File(file)
                    : this.downloadService.downloadFile(file)
            )
        );
    }

    private createFileName(file: IFile, suffix: string) {
        const fileName = file.fileName || file.name;
        if (!fileName) {
            return suffix || 'download';
        }

        const parts = fileName.split('.');
        if (suffix && parts.length > 1) {
            return `${suffix}.${parts[parts.length - 1]}`;
        }

        return suffix || fileName;
    }
}
